import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChangePasswordPanel() {
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setSuccess(false);
    setError('');
  };

  const toggle = (k) => setShow((p) => ({ ...p, [k]: !p[k] }));

  const newPw = form.new_password;
  const checks = {
    length: newPw.length >= 8,
    upper: /[A-Z]/.test(newPw),
    digit: /\d/.test(newPw),
    special: /[^A-Za-z0-9]/.test(newPw),
    match: newPw.length > 0 && newPw === form.confirm_password,
    different: newPw.length > 0 && newPw !== form.current_password,
  };
  const strengthScore = ['length', 'upper', 'digit', 'special'].filter((k) => checks[k]).length;
  const strengthLabel = ['Too weak', 'Weak', 'Fair', 'Strong', 'Excellent'][strengthScore];
  const strengthColor = ['#D5D5D5', '#E63946', '#FFB703', '#22C55E', '#16A34A'][strengthScore];

  const canSubmit =
    form.current_password.length > 0 &&
    checks.length &&
    checks.different &&
    checks.match &&
    !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!checks.length) return setError('New password must be at least 8 characters');
    if (!checks.different) return setError('New password must be different from current password');
    if (!checks.match) return setError('New password and confirmation do not match');

    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: form.current_password,
          new_password: form.new_password,
        },
        { withCredentials: true }
      );
      setSuccess(true);
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const PasswordField = ({ label, name, value, visible, onToggle, autoComplete, testId }) => (
    <div>
      <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#525252] mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={handleChange}
          autoComplete={autoComplete}
          data-testid={testId}
          className="w-full px-4 py-3 pr-12 bg-white border border-[#E5E5E5] text-[#1A1A2E] focus:border-[#E63946] focus:outline-none transition-colors"
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          data-testid={`${testId}-toggle`}
          className="absolute inset-y-0 right-0 px-3 text-[#8A8A8A] hover:text-[#E63946] transition-colors"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <section
      className="bg-white border border-[#E5E5E5] p-6 sm:p-8"
      data-testid="change-password-panel"
    >
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#E5E5E5]">
        <div className="w-10 h-10 bg-[#FFF0F1] flex items-center justify-center">
          <KeyRound size={20} className="text-[#E63946]" />
        </div>
        <div>
          <h2 className="text-xl font-black text-[#1A1A2E] tracking-tight">Change Password</h2>
          <p className="text-xs text-[#8A8A8A] mt-0.5">
            Rotate your admin password regularly to keep the site secure.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 max-w-xl">
        <PasswordField
          label="Current Password"
          name="current_password"
          value={form.current_password}
          visible={show.current}
          onToggle={() => toggle('current')}
          autoComplete="current-password"
          testId="current-password-input"
        />

        <PasswordField
          label="New Password"
          name="new_password"
          value={form.new_password}
          visible={show.next}
          onToggle={() => toggle('next')}
          autoComplete="new-password"
          testId="new-password-input"
        />

        {newPw.length > 0 && (
          <div className="space-y-2" data-testid="password-strength">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-[#F0F0F0] overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${(strengthScore / 4) * 100}%`,
                    backgroundColor: strengthColor,
                  }}
                />
              </div>
              <span
                className="text-xs font-bold tracking-[0.15em] uppercase"
                style={{ color: strengthColor }}
              >
                {strengthLabel}
              </span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#525252]">
              <li className={checks.length ? 'text-[#16A34A]' : ''}>
                {checks.length ? '✓' : '○'} At least 8 characters
              </li>
              <li className={checks.upper ? 'text-[#16A34A]' : ''}>
                {checks.upper ? '✓' : '○'} Contains uppercase letter
              </li>
              <li className={checks.digit ? 'text-[#16A34A]' : ''}>
                {checks.digit ? '✓' : '○'} Contains number
              </li>
              <li className={checks.special ? 'text-[#16A34A]' : ''}>
                {checks.special ? '✓' : '○'} Contains special character
              </li>
            </ul>
          </div>
        )}

        <PasswordField
          label="Confirm New Password"
          name="confirm_password"
          value={form.confirm_password}
          visible={show.confirm}
          onToggle={() => toggle('confirm')}
          autoComplete="new-password"
          testId="confirm-password-input"
        />

        {form.confirm_password.length > 0 && !checks.match && (
          <p className="text-xs text-[#E63946]" data-testid="password-mismatch-error">
            Passwords do not match
          </p>
        )}

        {error && (
          <div
            className="flex items-start gap-2 p-3 bg-[#FFF0F1] border border-[#E63946] text-[#E63946] text-sm"
            data-testid="change-password-error"
          >
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div
            className="flex items-start gap-2 p-3 bg-[#F0FDF4] border border-[#16A34A] text-[#15803D] text-sm"
            data-testid="change-password-success"
          >
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
            <span>Password changed successfully. Your session has been refreshed.</span>
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="change-password-submit"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                UPDATING...
              </>
            ) : (
              <>
                <KeyRound size={16} />
                UPDATE PASSWORD
              </>
            )}
          </button>
        </div>
      </form>
    </section>
  );
}

export default ChangePasswordPanel;
